// Villager gossip & reputation (MB.7). PURE (ENG-1).
//
// Each villager remembers "gossip" about the player: strongly positive from
// curing a zombie villager, mildly positive from trading, negative from harming
// villagers. A weighted sum is the player's reputation with that villager —
// positive reputation discounts its trades (feeding the `heroDiscount` fraction
// economy.ts already accepts), negative reputation only removes discounts (we
// never surcharge). Gossip decays toward zero over time and spreads between
// villagers who meet, so a good (or bad) name travels through a village.
//
// Data-light by design: the weights + per-type caps ARE the model; the host
// keeps one small ledger per villager (persisted with the entity).

export type GossipType =
  | 'major_positive'   // cured a zombie villager
  | 'minor_positive'   // small kindnesses (reserved)
  | 'trading'          // traded with this villager
  | 'minor_negative'   // hit a villager
  | 'major_negative';  // killed a villager

interface GossipKind {
  /** Multiplier on the stored value when summing reputation. */
  weight: number;
  /** Per-type storage cap (Java's limits, our subset). */
  max: number;
  /** Adds to reputation when true, subtracts when false. */
  positive: boolean;
  /** Amount removed per decay step. */
  decay: number;
}

export const GOSSIP_KINDS: Record<GossipType, GossipKind> = {
  major_positive: { weight: 5, max: 20, positive: true, decay: 2 },
  minor_positive: { weight: 1, max: 200, positive: true, decay: 20 },
  trading: { weight: 1, max: 25, positive: true, decay: 2 },
  minor_negative: { weight: 5, max: 200, positive: false, decay: 20 },
  major_negative: { weight: 5, max: 100, positive: false, decay: 2 },
};

export const GOSSIP_TYPES = Object.keys(GOSSIP_KINDS) as GossipType[];

/** One villager's gossip about one player: type → stored value (missing = 0). */
export type GossipLedger = Partial<Record<GossipType, number>>;

/** Add `amount` of a gossip type, clamped to that type's cap (never below 0). */
export function addGossip(ledger: GossipLedger, type: GossipType, amount: number): GossipLedger {
  const kind = GOSSIP_KINDS[type];
  const next = Math.max(0, Math.min(kind.max, (ledger[type] ?? 0) + amount));
  return { ...ledger, [type]: next };
}

/** Signed reputation: positive kinds add weight·value, negative kinds subtract. */
export function reputation(ledger: GossipLedger): number {
  let sum = 0;
  for (const t of GOSSIP_TYPES) {
    const v = ledger[t] ?? 0;
    const k = GOSSIP_KINDS[t];
    sum += (k.positive ? 1 : -1) * v * k.weight;
  }
  return sum;
}

/** One decay step (Java nudges each gossip toward 0 over time). Floors at 0 and
 *  drops emptied entries so the ledger stays sparse. */
export function decayGossip(ledger: GossipLedger): GossipLedger {
  const out: GossipLedger = {};
  for (const t of GOSSIP_TYPES) {
    const next = Math.max(0, (ledger[t] ?? 0) - GOSSIP_KINDS[t].decay);
    if (next > 0) out[t] = next;
  }
  return out;
}

/** Two villagers who meet share gossip: each keeps the larger of the two values
 *  per type, so a reputation spreads through the village. Returns both ledgers. */
export function shareGossip(a: GossipLedger, b: GossipLedger): [GossipLedger, GossipLedger] {
  const merge = (x: GossipLedger, y: GossipLedger): GossipLedger => {
    const out: GossipLedger = {};
    for (const t of GOSSIP_TYPES) {
      const m = Math.max(x[t] ?? 0, y[t] ?? 0);
      if (m > 0) out[t] = m;
    }
    return out;
  };
  return [merge(a, b), merge(b, a)];
}

/** reputation 100 (a full cure) → 0.30 discount. */
export const REP_DISCOUNT_PER_POINT = 0.003;
export const MAX_TRADE_DISCOUNT = 0.5;

/** Positive reputation → a 0..1 trade-discount fraction (the `heroDiscount`
 *  economy.ts takes). Non-positive reputation → 0 (we never surcharge). */
export function gossipDiscount(rep: number): number {
  if (rep <= 0) return 0;
  return Math.min(MAX_TRADE_DISCOUNT, rep * REP_DISCOUNT_PER_POINT);
}

/** The discounted first-ingredient count for an offer whose base first-buy is
 *  `baseCount`, given a 0..1 `discount`. Never below 1 — a trade always costs. */
export function discountedBuyCount(baseCount: number, discount: number): number {
  const d = Math.max(0, Math.min(1, discount));
  return Math.max(1, baseCount - Math.floor(baseCount * d));
}
